import React, { useState } from 'react';
import { Share2, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Reusable share button.
 * - Uses the native Web Share API on supported browsers (mobile → opens the
 *   native sheet with WhatsApp, IG, Telegram, SMS, etc.).
 * - Falls back to clipboard copy on desktop / browsers without Web Share.
 *
 * Props:
 *  - url:       absolute URL to share (defaults to current location.href)
 *  - title:     short title used by Web Share + clipboard preview
 *  - text:      optional extra body text passed to Web Share
 *  - label:     button label (default: "Share")
 *  - variant:   "outline" (default, matches Wishlist style) | "ghost" (icon only)
 *  - testid:    data-testid for the button
 *  - className: extra classes for layout
 */
export default function ShareButton({
  url,
  title = '',
  // `text` is intentionally accepted but no longer forwarded to
  // navigator.share() — see handleShare for why. Keeping the prop in the
  // signature so existing call sites don't need to change.
  // eslint-disable-next-line no-unused-vars
  text = '',
  label = 'Share',
  variant = 'outline',
  testid = 'share-btn',
  className = '',
}) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
    // We intentionally DO NOT pass the `text` field to navigator.share() —
    // many share targets (WhatsApp on iOS especially) concatenate `text` and
    // `url` into a single message body, which made the shared link appear
    // twice in the chat. Passing only `{title, url}` lets the receiving app
    // render the rich preview (Open Graph card) cleanly without any
    // duplication. `title` is kept because it shows nicely in the iOS
    // native share sheet header.
    const payload = { title: title || document.title, url: shareUrl };
    // Web Share API (mobile-first). Cancelling the sheet throws AbortError which
    // we silently swallow.
    if (navigator.share) {
      try {
        await navigator.share(payload);
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
        // fall through to clipboard fallback if the native share fails for any
        // other reason (e.g. permissions denied)
      }
    }
    // Clipboard fallback
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy link — please copy manually from the address bar.');
    }
  };

  if (variant === 'ghost') {
    return (
      <button
        type="button"
        onClick={handleShare}
        className={`inline-flex items-center justify-center rounded-full p-2 text-[#78716C] hover:text-[#2C2A29] hover:bg-cream-50 transition-all ${className}`}
        title={label}
        aria-label={label}
        data-testid={testid}
      >
        {copied ? <Check size={18} className="text-emerald-600" /> : <Share2 size={18} />}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      className={`w-full rounded-full px-6 py-3 font-body font-semibold transition-all flex items-center justify-center gap-2 border border-cream-100 bg-transparent text-[#2C2A29] hover:border-[#F4C2C2] hover:bg-[#FFF5F5] ${className}`}
      data-testid={testid}
    >
      {copied ? <><Check size={18} className="text-emerald-600" /> Link copied</> : <><Share2 size={18} /> {label}</>}
    </button>
  );
}
